import DOMPurify from "dompurify";
import { marked } from "marked";
import { useMemo } from "react";

/**
 * Markdown renderer for assistant chat bubbles.
 *
 * Stripped-down vs. lemon_agent's `ChatMarkdown`: we don't yet have
 * a `/kb/image` endpoint to resolve bare absolute paths against, so
 * image src rewriting is disabled. Once `knowledge-service` exposes
 * an equivalent we can wire it up here.
 *
 * Output is HTML-sanitized via DOMPurify before being injected,
 * which is the price of using `dangerouslySetInnerHTML` to keep the
 * chat bubbles styled by `.prose-claude` rather than React-rendered
 * Markdown nodes.
 */
export interface ChatMarkdownProps {
  source: string;
}

export function ChatMarkdown({ source }: ChatMarkdownProps) {
  const html = useMemo(() => {
    const rendered = marked.parse(source, {
      gfm: true,
      breaks: true,
      async: false,
    }) as string;
    return DOMPurify.sanitize(rendered, {
      ADD_ATTR: ["target", "rel"],
    });
  }, [source]);

  return (
    <div
      className="prose-claude max-w-none text-sm leading-relaxed"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via DOMPurify above.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
